import { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';

import { PageSubHeader } from '@/components/organisms/PageSubHeader';
import { StepDefinition, StepWizard } from '@/components/organisms/StepWizard/StepWizard';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { UnifiedMessaging } from './Messaging';
import { UnifiedReview } from './Review';
import { UnifiedSetup } from './Setup';
import { CampaignBreadcrumbs } from '../../components/CampaignBreadcrumbs';
import { BuildPageLoadingState } from '../../components/CampaignLoadingState';
import { CampaignWrapper } from '../../components/CampaignWrapper';
import {
  useWizardStepControllerRegistry,
  WizardStepControllerProvider,
} from '../../hooks/useWizardStepController';
import {
  selectDraftAutonomousActions,
  selectDraftCampaignName,
  selectHasNbaConfig,
  selectSelectedCampaign,
} from '../../redux/selectors';

/** Step path segments in wizard order. Index maps to the StepWizard `currentStep`. */
const STEP_SEGMENTS = ['setup', 'messaging', 'review'] as const;
// Derived from STEP_SEGMENTS so the order is declared once (no drift if a step moves).
const STEP_INDEX: Record<string, number> = Object.fromEntries(
  STEP_SEGMENTS.map((segment, index) => [segment, index])
);

/**
 * Derive the active wizard step from a URL pathname (last segment). Anything that
 * isn't a known step segment (incl. trailing slashes / a bare campaign path)
 * falls back to step 0 (setup).
 */
export const getStepIndexFromPath = (pathname: string): number => {
  const segment = pathname.split('/').filter(Boolean).pop() ?? '';
  return STEP_INDEX[segment] ?? 0;
};

/**
 * Single mounted wizard for the unified campaign create/edit flow.
 *
 * The 3 steps (setup → messaging → review) live in one always-mounted
 * `<StepWizard>` so step transitions animate (the slide only works while one
 * instance stays mounted and `currentStep` changes). The active step is derived
 * synchronously from the URL — the URL is the single source of truth, so browser
 * back/forward "just work" with no `useState` mirror or `navigate`-in-effect.
 *
 * Rendered by a pathless layout route in `CampaignsRouter`; the matched leaf
 * (setup/messaging/review) renders nothing — this layout renders the step bodies.
 */
export const CampaignWizardLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { campaignId } = useParams<{ campaignId: string }>();
  const slug = useSelector(selectAccountSlug);

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'campaigns', actions.WRITE)
  );

  const selectedCampaign = useSelector(selectSelectedCampaign);
  const draftCampaignName = useSelector(selectDraftCampaignName);
  const hasNbaConfig = useSelector(selectHasNbaConfig);
  const autonomousActions = useSelector(selectDraftAutonomousActions);
  // Same NBA branch the Messaging delegator uses to pick its title.
  const isNba = hasNbaConfig || autonomousActions;

  // Derive the active step from the last path segment (fallback to setup).
  const currentStep = getStepIndexFromPath(location.pathname);

  // Imperative controller registry — the active step registers its handlers; the
  // wizard header dereferences them lazily (only inside click handlers). The
  // registry also owns `isStepLoading`: the active step reports its essential-data
  // loading so the wizard can disable the sticky header CTAs while the step body
  // shows its skeleton (the buttons live here, outside the step's keyed body).
  const { controllerRef, register, isStepLoading, setStepLoading } =
    useWizardStepControllerRegistry();

  const validateStep = useCallback(
    () => controllerRef.current?.validate?.() ?? Promise.resolve(true),
    [controllerRef]
  );
  const handleCancel = useCallback(() => controllerRef.current?.saveAndExit(), [controllerRef]);
  const handleComplete = useCallback(async () => {
    await controllerRef.current?.complete?.();
  }, [controllerRef]);

  const handleStepChange = useCallback(
    (next: number) => {
      // Absolute push (no replace) keeps back/forward history intact and lands NBA
      // and standard alike on the same unified `/review`.
      navigate(`/${slug}/campaigns/${campaignId}/${STEP_SEGMENTS[next]}`);
    },
    [navigate, slug, campaignId]
  );

  const messagingTitle = isNba
    ? t('campaigns.messaging.nba_title')
    : t('campaigns.messaging.title');

  // Exactly 3 steps so the "1 of 3" indicator is preserved. The existing
  // routers (Setup/Messaging/Review) branch NBA vs standard internally.
  const steps = useMemo<StepDefinition[]>(
    () => [
      {
        id: 'setup',
        title: t('campaigns.setup.title'),
        component: <UnifiedSetup />,
        validate: validateStep,
      },
      {
        id: 'messaging',
        title: messagingTitle,
        component: <UnifiedMessaging />,
        validate: validateStep,
      },
      {
        id: 'review',
        title: t('campaigns.review.title'),
        component: <UnifiedReview />,
        validate: validateStep,
      },
    ],
    [t, messagingTitle, validateStep]
  );

  // Top-level load gate: wait for THIS campaign before mounting the wizard so the
  // slide refs aren't reset by a later refetch. `selectedCampaign` is set once and
  // stays stable for the campaign, so this never flips back mid-flow. Per-step
  // loading guards (senders/brains, draft completeness) stay inside the steps.
  const isReady = !!selectedCampaign && selectedCampaign.campaign_id === campaignId;

  return (
    <>
      <PageSubHeader breadcrumbs={<CampaignBreadcrumbs campaignName={draftCampaignName} />} />
      {/* `pt-0` drops the scroll container's default top padding so the sticky
          header bar pins flush to the scroll top — without it, `sticky top-0`
          pins below the 20px padding and step content peeks in that gap. */}
      <PageContentWrapper readOnly={!canWrite} className="[scrollbar-gutter:stable] pt-0">
        <CampaignWrapper>
          {isReady ? (
            <WizardStepControllerProvider register={register} setStepLoading={setStepLoading}>
              <StepWizard
                steps={steps}
                currentStep={currentStep}
                onStepChange={handleStepChange}
                onCancel={canWrite ? handleCancel : undefined}
                onComplete={handleComplete}
                isLoading={isStepLoading}
                canProceed={canWrite}
                completeLabel={t('campaigns.review.review_and_start_header')}
                secondaryLabel={t('campaigns.save_and_exit')}
                stickyHeader
                className="w-full"
              >
                <StepWizard.Steps />
              </StepWizard>
            </WizardStepControllerProvider>
          ) : (
            <BuildPageLoadingState />
          )}
        </CampaignWrapper>
      </PageContentWrapper>
    </>
  );
};
